import { defineStore } from 'pinia'
import { useRoute, useRouter } from 'vue-router'

import { computed, reactive, ref } from 'vue'

import { AuthApi } from '@/features/api/services/authApi.js'
import { CLUB_SELECTOR_PATH } from '@/features/auth/clubSelfRegistrationConfig.js'
import { decideRedirectionPointByRole } from '@/features/auth/decideRedirectionPointByRole.js'
import { getClubSelectorPushQuery, useAuthRedirection } from '@/features/auth/useAuthRedirection.js'
import { pickInvitationQueryForRoute } from '@/features/auth/utils/authInvitationQuery.js'
import { applyPostLoginInvitationIfPresent } from '@/features/auth/utils/postLoginInvitationNavigation.js'
import {
  AUTH_FLOW,
  addAuthFlowBreadcrumb,
  reportAuthFlowError,
  resolveLoginApiErrorI18nKey,
} from '@/features/auth/utils/reportAuthFlowError.js'
import { isSuperAdminProfileType } from '@/features/currentUser/constants'
import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { useRegisterStore } from '@/features/auth/views/register/useRegisterStore.js'
import { useClubRouter } from '@/features/router/useClubRouer.js'
import { useToaster } from '@/features/ui/toaster/useToaster'

export const UserIsNotExist = 'User_Is_Not_Exist'
export const AccountIsNotExist = 'Account_Is_Not_Exist'

export const AlreadyRegisteredThroughGoogleMessage =
  'User_Has_Been_Already_Registered_Through_Google'

function getLoginStepLabel(step) {
  switch (step) {
    case 1:
      return 'email-enter'
    case 2:
      return 'password-enter'
    case 3:
      return 'google-login-warning'
    default:
      return String(step)
  }
}

export const useLoginStore = defineStore('loginStore', function () {
  const authStore = useAuthStore()
  const registerStore = useRegisterStore()
  const isLoading = ref(false)
  const redirectCallback = ref(null)
  const loginEmailEnterUserIsNotExistCallback = ref(null)

  const STEPS = {
    EMAIL_ENTER: 1,
    PASSWORD_ENTER: 2,
    GOOGLE_LOGIN_WARNING: 3,
  }
  const currentStep = ref(STEPS.EMAIL_ENTER)
  const needsOTP = ref(false)

  const isEmailEnterStep = computed(() => currentStep.value === STEPS.EMAIL_ENTER)
  const isPasswordEnterStep = computed(() => currentStep.value === STEPS.PASSWORD_ENTER)
  const isGoogleLoginWarningView = computed(() => currentStep.value === STEPS.GOOGLE_LOGIN_WARNING)

  const errorText = ref('')

  const formValues = reactive({
    email: '',
    password: '',
    otpCode: '',
    rememberMe: false,
  })

  const { redirectUrl } = useAuthRedirection()
  const route = useRoute()

  const router = useClubRouter()
  const vueRouter = useRouter()
  const toaster = useToaster()

  async function handleEmailEnter() {
    errorText.value = ''

    try {
      isLoading.value = true

      const response = await AuthApi.loginWithEmail({
        email: formValues.email,
        password: formValues.password,
      })

      if (response.codeSent) {
        needsOTP.value = true
        currentStep.value = STEPS.PASSWORD_ENTER
        addAuthFlowBreadcrumb({
          flow: AUTH_FLOW.LOGIN,
          step: getLoginStepLabel(STEPS.PASSWORD_ENTER),
          message: '[Auth] login advanced to otp',
        })
        return
      }

      if (!response.auth_token) {
        reportAuthFlowError({
          flow: AUTH_FLOW.LOGIN,
          step: getLoginStepLabel(STEPS.EMAIL_ENTER),
          reason: 'missing-auth-token',
        })
        toaster.error({
          title: 'Error',
          detail: 'No auth token in response',
        })
        return
      }

      await authStore.setUserToken(response.auth_token, {
        email: formValues.email,
        password: formValues.password,
      })

      await decideNavigation()
    } catch (e) {
      const errorData = e?.response?.data
      const step = getLoginStepLabel(currentStep.value)

      if (errorData === UserIsNotExist || errorData === AccountIsNotExist) {
        registerStore.setEmail(formValues.email)

        if (loginEmailEnterUserIsNotExistCallback.value) {
          loginEmailEnterUserIsNotExistCallback.value({ errorData })
          return
        }

        addAuthFlowBreadcrumb({
          flow: AUTH_FLOW.LOGIN,
          step,
          message: '[Auth] login redirect to register',
          data: { errorData },
        })

        await router.push({
          path: '/auth/register',
          query: pickInvitationQueryForRoute({
            ...route.query,
            email: formValues.email,
            redirectUrl,
          }),
        })
        return
      }

      errorText.value = resolveLoginApiErrorI18nKey(e)

      if (errorData === AlreadyRegisteredThroughGoogleMessage) {
        setCurrentStep(STEPS.GOOGLE_LOGIN_WARNING)
      }

      reportAuthFlowError({
        flow: AUTH_FLOW.LOGIN,
        step,
        error: e,
        errorCode: errorData,
        i18nKey: errorText.value,
      })
    } finally {
      isLoading.value = false
    }
  }

  async function decideNavigation(options = {}) {
    const myDetailsResponse = await authStore.getMyDetailsWithoutCache(false)

    if (redirectCallback.value) {
      redirectCallback.value({ myDetailsResponse })
      return
    }

    if (isSuperAdminProfileType(myDetailsResponse?.type)) {
      const url = decideRedirectionPointByRole(myDetailsResponse.type)
      console.log('Redirection to point ->', url)
      await router.push(url)
      return
    }

    let omitInvitationFromRoute = options.omitInvitationFromRoute

    if (!omitInvitationFromRoute) {
      const invitationNavigation = await applyPostLoginInvitationIfPresent({
        routeQuery: route.query,
        redirectUrl,
        vueRouter,
      })

      if (invitationNavigation.handled) {
        return
      }
    }

    console.log('redirectUrl: ', redirectUrl)

    if (!myDetailsResponse) {
      reportAuthFlowError({
        flow: AUTH_FLOW.LOGIN,
        step: 'decide-navigation',
        reason: 'missing-my-details',
      })
      toaster.error({
        title: 'Error',
        detail: 'No myDetailsResponse',
      })

      return
    }

    const roleBasedPath = decideRedirectionPointByRole(myDetailsResponse.type)

    if (roleBasedPath !== CLUB_SELECTOR_PATH) {
      console.log('Redirection to point ->', roleBasedPath)
      await router.push(roleBasedPath)
      return
    }

    console.log('Redirection to club selector')
    await vueRouter.push({
      path: CLUB_SELECTOR_PATH,
      query: getClubSelectorPushQuery(route.query, {
        omitInvitation: omitInvitationFromRoute,
      }),
    })
  }

  async function handlePasswordEnter() {
    try {
      isLoading.value = true
      const loginResponse = await AuthApi.loginWithEmail({
        email: formValues.email,
        password: formValues.password,
        code: formValues.otpCode,
      })

      if (!loginResponse.auth_token) {
        reportAuthFlowError({
          flow: AUTH_FLOW.LOGIN,
          step: getLoginStepLabel(STEPS.PASSWORD_ENTER),
          reason: 'missing-auth-token',
        })
        toaster.error({
          title: 'Error',
          detail: 'No auth token in response',
        })
        return
      }

      await authStore.setUserToken(loginResponse.auth_token, {
        email: formValues.email,
        password: formValues.password,
      })

      await decideNavigation()
    } catch (e) {
      const errorData = e?.response?.data
      const step = getLoginStepLabel(currentStep.value)

      if (errorData === 'Invalid_Username_Or_Password') {
        currentStep.value = STEPS.EMAIL_ENTER
      }

      errorText.value = resolveLoginApiErrorI18nKey(e)
      formValues.otpCode = ''

      reportAuthFlowError({
        flow: AUTH_FLOW.LOGIN,
        step,
        error: e,
        errorCode: errorData,
        i18nKey: errorText.value,
      })
    } finally {
      isLoading.value = false
    }
  }

  function handleClickStepBack() {
    if (isPasswordEnterStep.value) {
      currentStep.value = STEPS.EMAIL_ENTER
      formValues.otpCode = ''
    }
  }

  function setFormValues(values) {
    Object.assign(formValues, values)
  }

  function setCurrentStep(step) {
    currentStep.value = step
  }

  function setRedirectCallback(callback) {
    redirectCallback.value = callback
  }

  function setLoginEmailEnterUserIsNotExistCallback(callback) {
    loginEmailEnterUserIsNotExistCallback.value = callback
  }

  return {
    STEPS,
    setCurrentStep,
    currentStep,

    isEmailEnterStep,
    isPasswordEnterStep,
    isGoogleLoginWarningView,

    handleEmailEnter,
    handlePasswordEnter,

    handleClickStepBack,

    setFormValues,
    formValues,

    isLoading,

    needsOTP,

    setRedirectCallback,
    redirectCallback,

    setLoginEmailEnterUserIsNotExistCallback,
    loginEmailEnterUserIsNotExistCallback,

    decideNavigation,

    errorText,
  }
})
